import { useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { DetailsContent } from "components/app_layout/DetailsLayout";
import { CreateEntityDrawer } from "components/entities/CreateEntityDrawer";
import { CreateLeadForm } from "components/leads/create_lead/CreateLeadForm";
import LeadPipelineEmptyState from "components/leads/empty_states/LeadPipelineEmptyState";
import { FilterableLeads } from "components/leads/leads_table/FilterableLeads";
import {
  DEFAULT_PAGINATION,
  EMPTY_LEAD_FILTERS,
  getDefaultLeadStageIds,
  LeadFilters,
} from "components/leads/leads_table/utils";
import Button from "components/ui/button/Button";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { useLeads } from "lib/hooks/api/leads/useLeads";
import { useLeadWorkflow } from "lib/hooks/api/leads/useLeadWorkflow";
import { StandardViewName } from "lib/hooks/api/notices/views/useStandardViewTarget";
import { useLogSearchOfURLFilters } from "lib/hooks/api/userActivity/useLogSearch";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { EventNames, useTracking } from "lib/tracking";
import { LoggedEntityType } from "lib/types/graphQLEnums";

export function LeadsPage() {
  useLogSearchOfURLFilters(LoggedEntityType.Lead, "leadFilters");
  const { logEvent } = useTracking();
  const { data: currentUser } = useCurrentUser();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { data: leadWorkflowData, isLoading: isLoadingWorkflowData } = useLeadWorkflow();

  const defaultFilters: LeadFilters = leadWorkflowData
    ? {
        ...EMPTY_LEAD_FILTERS,
        stageIds: getDefaultLeadStageIds(leadWorkflowData.transitions),
        // A non user shouldn't get here, but can't use `useAssertCurrentUser`
        // here because it makes external bids sharing fail
        assignee: currentUser?.guid ? { ids: [currentUser?.guid], includeNulls: false } : undefined,
      }
    : EMPTY_LEAD_FILTERS;

  const { data: initialLeads, isLoading: isLoadingInitialLeads } = useLeads({}, DEFAULT_PAGINATION);

  if (isLoadingWorkflowData || isLoadingInitialLeads) {
    return <CentredSpinner />;
  }

  const isPipelineEmpty = initialLeads?.total === 0;

  return (
    <PageContent>
      <LeadsContainer>
        <Flex justifyContent="space-between">
          <Text h1>Leads</Text>
          <Button
            onClick={() => {
              setIsCreateModalOpen(true);
              logEvent(EventNames.createLeadStarted, {
                "Context source": "Create lead modal",
              });
            }}
          >
            Create Lead
          </Button>
        </Flex>
        {isPipelineEmpty ? (
          <Wrapper>
            <LeadPipelineEmptyState
              eventData={{
                "Context source": "Leads page",
              }}
              description="Browse expiring notices to uncover deals worth chasing. Save them to your Lead pipeline to activate AI-powered qualification and start engaging with the buyer"
              actionButtonTitle="Browse expiring notices"
              viewName={StandardViewName.Expiries}
              heading="Your pipeline's waiting!"
              helpUrl="https://help.stotles.com/faqs#how-to-build-pipeline-with-opportunities"
              learnMoreText="Learn more about Leads"
            />
          </Wrapper>
        ) : (
          <FilterableLeads defaultFilters={defaultFilters} />
        )}

        <CreateEntityDrawer
          entityType="lead"
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
        >
          <CreateLeadForm
            defaultValues={{ assignedToId: currentUser?.guid }}
            onComplete={() => setIsCreateModalOpen(false)}
            trackingData={{
              "Context source": "Leads page",
            }}
            navigateToNewLead
          />
        </CreateEntityDrawer>
      </LeadsContainer>
    </PageContent>
  );
}

const PageContent = styled(DetailsContent)({
  paddingTop: "24px",
  display: "flex",
  flexDirection: "column",
  height: "100%",
});

const Wrapper = styled(Flex)(({ theme }) => ({
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderRadius: "4px",
  height: "100%",
  justifyContent: "center",
  alignItems: "center",
}));

const LeadsContainer = styled(Flex)({
  flex: 1,
  flexDirection: "column",
  gap: "16px",
});

export default LeadsPage;
